import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import MyApiClient, { impostaProfiloDiModifica } from '../../../../../api-client/MyApiClient';
import { fiscalRegime, italianProvinces } from '../../../../components/Util/enum';
import { sidSelectStyles } from '../../../../components/Util/sidSelectStyles';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';
import chiediConferma from '../../../../components/Util/chiediConferma';
import VideoPresentazione from '../../../../components/Struttura/SchedaApp/VideoPresentazione';
import { usePageTitle, useStatoInSolaLettura } from '../../../../layouts/PageHeaderExtra';

/*
 * Validazione della richiesta di fatturazione di una struttura (super admin). Una lista di campi, uno sotto l'altro, compatta:
 * prima i dati della richiesta (in evidenza), poi i dati della struttura. Tutti modificabili.
 * In fondo, nell'ordine: Annulla, Rifiuta, Convalida, Salva.
 * Salvare le modifiche usa PATCH /administration/profiles/{userId} (sportid-docs, «modifiche 12 ottobre», cap. 3).
 */

const LISTA_RICHIESTE = '/superadmin/billing/requests';

const TIPOLOGIE = [
    { value: 'ASD', label: 'ASD' }, { value: 'SSD', label: 'SSD' },
    { value: 'INDIVIDUAL_COMPANY', label: 'Ditta individuale' }, { value: 'CAPITAL_COMPANY', label: 'SRL / SPA' },
];
const NATURE = [
    { value: 'SCHOOL', label: 'Scuola' }, { value: 'GYM', label: 'Palestra' }, { value: 'RENTAL_SHOP', label: 'Rental Shop' },
    { value: 'CIRCLE', label: 'Club' }, { value: 'SPORTS_CLUB', label: 'Società Sportiva' },
];
const SI_NO = [{ value: 'true', label: 'Sì' }, { value: 'false', label: 'No' }];

// [chiave, etichetta, tipo, opzioni]
const RICHIESTA = [
    ['ragione_sociale', 'Ragione sociale'], ['partita_iva', 'Partita IVA'], ['codice_fiscale', 'Codice fiscale'],
    ['fiscal_regime', 'Regime fiscale', 'select', fiscalRegime], ['vat_section', 'Sezionale'],
    ['legge_398_91', 'Legge 398/91', 'select', SI_NO], ['pec', 'PEC'],
    ['indirizzo', 'Indirizzo'], ['cap', 'CAP'], ['citta', 'Città'], ['provincia', 'Provincia', 'select', italianProvinces], ['nazione', 'Nazione'],
];
const STRUTTURA = [
    ['name', 'Nome della struttura'], ['phoneNumber', 'Telefono'], ['email', 'Email di accesso', 'bloccato'],
    ['clubNature', 'Tipologia giuridica', 'select', TIPOLOGIE], ['clubSubNature', 'Natura', 'select', NATURE], ['incorporationYear', 'Anno di costituzione'],
    ['repFirstName', 'Referente: nome'], ['repLastName', 'Referente: cognome'], ['repTaxCode', 'Referente: codice fiscale'],
    ['repBirthPlace', 'Referente: luogo di nascita'], ['repCity', 'Referente: città'], ['repAddress', 'Referente: indirizzo'],
    ['streetAddress', 'Sede operativa: via'], ['locality', 'Sede operativa: comune'], ['province', 'Sede operativa: provincia', 'select', italianProvinces], ['postalCode', 'Sede operativa: CAP'],
    ['legalStreetAddress', 'Sede legale: via'], ['legalLocality', 'Sede legale: comune'], ['legalProvince', 'Sede legale: provincia', 'select', italianProvinces], ['legalPostalCode', 'Sede legale: CAP'],
];

const STATI = {
    WAIT_RESPONSE: 'In attesa', CONFIRMED: 'Confermata', REJECTED: 'Rifiutata',
};

// Partita IVA italiana: 11 cifre con cifra di controllo (se non e` valida il servizio SDI rifiuta la registrazione e la convalida fallisce)
const partitaIvaValida = (v) => {
    if (!/^\d{11}$/.test(v)) return false;
    let somma = 0;
    for (let i = 0; i < 11; i += 1) {
        let c = Number(v[i]);
        if (i % 2 === 1) { c *= 2; if (c > 9) c -= 9; }
        somma += c;
    }
    return somma % 10 === 0;
};

const token = () => { try { return JSON.parse(sessionStorage.getItem('userDetails')).idToken; } catch { return ''; } };
const dataBreve = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

// `lista`: dove si torna; `conElimina`/`conVideo`: la stessa pagina e` la scheda della struttura sportiva (super admin › Strutture sportive)
export const ValidazioneRichiestaFatturazione = ({ lista = LISTA_RICHIESTE, conElimina = false, conVideo = false }) => {
    const LISTA = lista;
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [f, setF] = useState({});
    const [iniziale, setIniziale] = useState('');
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState('');
    const [rifiuto, setRifiuto] = useState(false);
    const [motivo, setMotivo] = useState('');

    const club = profile?.club;
    const richiesta = club?.invoiceDataSender;
    const stato = richiesta?.status || '';

    usePageTitle(club?.name ? `${club.name}` : undefined);
    useStatoInSolaLettura(STATI[stato] || '', stato === 'CONFIRMED' ? 'attivo' : stato === 'REJECTED' ? 'non-attivo' : 'bozza');

    // Con la scheda aperta, il salvataggio del profilo va alla rotta di amministrazione di questa struttura
    useEffect(() => {
        impostaProfiloDiModifica(id);
        return () => impostaProfiloDiModifica(null);
    }, [id]);

    useEffect(() => {
        let vivo = true;
        new MyApiClient().getProfiles(token(), id).then((r) => {
            if (!vivo) return;
            if (!r || r.error || !r.club) { setErrore((r && r.error) || 'Struttura non trovata.'); setProfile({}); return; }
            const c = r.club;
            const d = c.invoiceDataSender || {};
            const l = c.legalAddress || {};
            const dati = {
                ragione_sociale: d.denomination || c.name || '', partita_iva: d.vat_number || c.vatNumber || '', codice_fiscale: d.tax_code || c.taxCode || '',
                fiscal_regime: d.fiscal_regime || '', vat_section: d.vat_section || '', pec: d.pec || '',
                legge_398_91: c.legge_398_91 === true ? 'true' : c.legge_398_91 === false ? 'false' : '',
                indirizzo: d.address || l.streetAddress || '', cap: d.zip_code || l.postalCode || '', citta: d.city || l.locality || '',
                provincia: d.province || l.province || '', nazione: d.nation || l.country || 'IT',
                name: c.name || '', phoneNumber: c.phoneNumber || '', email: c.email || '', clubNature: c.clubNature || '', clubSubNature: c.clubSubNature || '',
                incorporationYear: c.incorporationYear || '',
                repFirstName: c.legalRepresentativeFirstName || '', repLastName: c.legalRepresentativeLastName || '', repTaxCode: c.legalRepresentativeTaxCode || '',
                repBirthPlace: c.legalRepresentativeBirthPlace || '', repCity: c.legalRepresentativeCity || '', repAddress: c.legalRepresentativeAddress || '',
                streetAddress: c.address?.streetAddress || '', locality: c.address?.locality || '', province: c.address?.province || '', postalCode: c.address?.postalCode || '',
                legalStreetAddress: l.streetAddress || '', legalLocality: l.locality || '', legalProvince: l.province || '', legalPostalCode: l.postalCode || '',
            };
            setProfile(r);
            setF(dati);
            setIniziale(JSON.stringify(dati));
        });
        return () => { vivo = false; };
    }, [id]);

    const modificato = JSON.stringify(f) !== iniziale;
    const imposta = (chiave) => (v) => setF((prima) => ({ ...prima, [chiave]: v?.target ? v.target.value : (v?.value ?? '') }));

    const riga = ([chiave, etichetta, tipo, opzioni]) => {
        if (chiave === 'legge_398_91' && !['ASD', 'SSD'].includes(f.clubNature)) return null;
        const idCampo = `val-${chiave}`;
        return (
            <div className="sid-validazione__riga" key={chiave}>
                <Form.Label htmlFor={idCampo}>{etichetta}</Form.Label>
                {tipo === 'select' ? (
                    <Select
                        inputId={idCampo}
                        styles={sidSelectStyles}
                        options={opzioni}
                        value={opzioni.find((o) => o.value === f[chiave]) || null}
                        onChange={imposta(chiave)}
                        noOptionsMessage={() => 'Nessun risultato'}
                    />
                ) : (
                    <Form.Control id={idCampo} value={f[chiave] ?? ''} disabled={tipo === 'bloccato'} onChange={imposta(chiave)} />
                )}
            </div>
        );
    };

    // Campi della richiesta che servono per convalidarla
    const mancanti = () => {
        const obbligatori = [['ragione_sociale', 'la ragione sociale'], ['codice_fiscale', 'il codice fiscale'], ['indirizzo', "l'indirizzo"], ['cap', 'il CAP'], ['citta', 'la città'], ['provincia', 'la provincia']];
        if (f.clubNature !== 'ASD') obbligatori.push(['partita_iva', 'la partita IVA']);
        if (f.clubNature !== 'CAPITAL_COMPANY') obbligatori.push(['fiscal_regime', 'il regime fiscale']);
        if (['ASD', 'SSD'].includes(f.clubNature)) obbligatori.push(['legge_398_91', 'la scelta sulla legge 398/91']);
        const lista = obbligatori.filter(([k]) => !String(f[k] ?? '').trim()).map(([k, t]) => ({ campo: `val-${k}`, messaggio: `Manca ${t}` }));
        const piva = String(f.partita_iva ?? '').trim();
        if (piva && !partitaIvaValida(piva)) lista.push({ campo: 'val-partita_iva', messaggio: 'La partita IVA non è valida: servono 11 cifre con la cifra di controllo corretta' });
        const cf = String(f.codice_fiscale ?? '').trim();
        if (cf && !/^\d{11}$/.test(cf) && !/^[A-Za-z0-9]{16}$/.test(cf)) lista.push({ campo: 'val-codice_fiscale', messaggio: 'Il codice fiscale deve avere 11 cifre o 16 caratteri' });
        if (String(f.cap ?? '').trim() && !/^\d{5}$/.test(String(f.cap).trim())) lista.push({ campo: 'val-cap', messaggio: 'Il CAP deve avere 5 cifre' });
        return lista;
    };

    const salvaModifiche = async () => {
        const p = JSON.parse(JSON.stringify(profile));
        const c = p.club;
        c.name = f.name; c.phoneNumber = f.phoneNumber; c.incorporationYear = f.incorporationYear; c.clubNature = f.clubNature; c.clubSubNature = f.clubSubNature;
        c.legalRepresentativeFirstName = f.repFirstName; c.legalRepresentativeLastName = f.repLastName; c.legalRepresentativeTaxCode = f.repTaxCode;
        c.legalRepresentativeBirthPlace = f.repBirthPlace; c.legalRepresentativeCity = f.repCity; c.legalRepresentativeAddress = f.repAddress;
        c.address = { streetAddress: f.streetAddress, locality: f.locality, country: c.address?.country || 'Italia', postalCode: f.postalCode, province: f.province };
        c.legalAddress = { streetAddress: f.legalStreetAddress, locality: f.legalLocality, country: c.legalAddress?.country || 'Italia', postalCode: f.legalPostalCode, province: f.legalProvince };
        c.legge_398_91 = ['ASD', 'SSD'].includes(f.clubNature) ? f.legge_398_91 === 'true' : false;
        c.invoiceDataSender = {
            invoice_enabled: true, country_code: 'IT', vat_number: f.partita_iva, tax_code: f.codice_fiscale, denomination: f.ragione_sociale, pec: f.pec,
            address: f.indirizzo, zip_code: f.cap, city: f.citta, province: f.provincia, nation: 'IT',
            fiscal_regime: f.clubNature === 'CAPITAL_COMPANY' ? 'RF01' : f.fiscal_regime, vat_section: f.vat_section,
        };
        delete c.logo;
        delete c.pictures;
        const r = await new MyApiClient().updateProfile(token(), p);
        if (r?.error) {
            setErrore(`Non è stato possibile salvare le modifiche (${r.error}). Salvare i dati di un'altra struttura richiede la rotta di amministrazione del backend.`);
            return false;
        }
        setIniziale(JSON.stringify(f));
        return true;
    };

    const salva = async () => {
        if (segnalaMancanti(mancanti()) > 0) return;
        setInCorso(true);
        try {
            // Dopo il salvataggio si torna all'elenco (set-ui schede-navigazione)
            if (await salvaModifiche()) navigate(LISTA);
        } finally { setInCorso(false); }
    };

    const convalida = async () => {
        if (segnalaMancanti(mancanti()) > 0) return;
        if (!(await chiediConferma({
            titolo: 'Convalidare la richiesta?',
            testo: `La generazione di ricevute e fatture si attiva per ${f.name || 'questa struttura'}${modificato ? ' e le modifiche vengono salvate' : ''}.`,
            conferma: 'Convalida',
            variante: 'success',
        }))) return;
        setInCorso(true);
        try {
            if (modificato && !(await salvaModifiche())) return;
            const r = await new MyApiClient().updateAdministrationClubInvoiceDataSenderStatus(token(), 'CONFIRMED', id);
            if (r?.error) {
                // Il backend risponde 502 quando il servizio di fatturazione elettronica (SDI) non accetta i dati della struttura
                setErrore(/502/.test(r.error)
                    ? 'Il servizio di fatturazione elettronica (SDI) non ha accettato i dati della struttura: controlla la partita IVA e il codice fiscale. La richiesta resta in attesa.'
                    : r.error);
                return;
            }
            navigate(LISTA);
        } finally { setInCorso(false); }
    };

    const rifiuta = async () => {
        if (!motivo.trim()) { segnalaMancanti([{ campo: 'val-motivo', messaggio: 'Scrivi il motivo del rifiuto' }]); return; }
        setInCorso(true);
        try {
            const r = await new MyApiClient().updateAdministrationClubInvoiceDataSenderStatus(token(), 'REJECTED', id, motivo.trim());
            if (r?.error) { setErrore(r.error); return; }
            setRifiuto(false);
            navigate(LISTA);
        } finally { setInCorso(false); }
    };

    const elimina = async () => {
        if (!(await chiediConferma({ titolo: 'Eliminare la struttura?', testo: `Eliminare la struttura «${club?.name || 'senza nome'}»? L'operazione non si può annullare.`, conferma: 'Elimina' }))) return;
        setInCorso(true);
        try {
            const esito = await new MyApiClient().deleteProfile(token(), id);
            if (esito) { setErrore(esito); return; }
            navigate(LISTA);
        } finally { setInCorso(false); }
    };

    if (!profile) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>
            <Modal show={rifiuto} onHide={() => setRifiuto(false)} centered>
                <Modal.Header closeButton><Modal.Title>Rifiutare la richiesta?</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Label htmlFor="val-motivo">Motivo del rifiuto *</Form.Label>
                    <Form.Control as="textarea" id="val-motivo" rows={4} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                    <Form.Text className="text-dark">La struttura lo legge e può correggere i dati e inviare una nuova richiesta.</Form.Text>
                </Modal.Body>
                <Modal.Footer className="justify-content-center sid-footer-conferma">
                    <Button className="btn btn-light btn-add" onClick={() => setRifiuto(false)}>Annulla</Button>
                    <Button className="btn btn-danger btn-add" onClick={rifiuta} disabled={inCorso}>Rifiuta</Button>
                </Modal.Footer>
            </Modal>

            <div className="sid-validazione">
                <section className="sid-validazione__blocco sid-validazione__blocco--evidenza">
                    <h5 className="sid-form-section">Richiesta di fatturazione</h5>
                    <div className="sid-validazione__riga">
                        <Form.Label as="span">Stato</Form.Label>
                        <span className="sid-validazione__valore">{STATI[stato] || 'Non inviata'}</span>
                    </div>
                    <div className="sid-validazione__riga">
                        <Form.Label as="span">Data richiesta</Form.Label>
                        <span className="sid-validazione__valore">{dataBreve(richiesta?.requestedAt)}</span>
                    </div>
                    {stato === 'REJECTED' && richiesta?.rejectionMessage && (
                        <div className="sid-validazione__riga">
                            <Form.Label as="span">Ultimo rifiuto</Form.Label>
                            <span className="sid-validazione__valore">{richiesta.rejectionMessage}</span>
                        </div>
                    )}
                    {RICHIESTA.map(riga)}
                </section>
                <section className="sid-validazione__blocco">
                    <h5 className="sid-form-section">Dati della struttura</h5>
                    {STRUTTURA.map(riga)}
                </section>
                {conVideo && (
                    <section className="sid-validazione__blocco">
                        <h5 className="sid-form-section">Video di presentazione</h5>
                        <VideoPresentazione clubId={id} superAdmin />
                    </section>
                )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
                {conElimina && <Button className="btn btn-danger btn-add me-auto" onClick={elimina} disabled={inCorso}>Elimina struttura</Button>}
                <Button className="btn btn-light btn-add" onClick={() => navigate(LISTA)} disabled={inCorso}>Annulla</Button>
                {(stato === 'WAIT_RESPONSE' || stato === 'CONFIRMED') && <Button className="btn btn-danger btn-add" onClick={() => { setMotivo(''); setRifiuto(true); }} disabled={inCorso}>Rifiuta</Button>}
                {(stato === 'WAIT_RESPONSE' || stato === 'REJECTED') && <Button className="btn btn-success btn-add" onClick={convalida} disabled={inCorso}>Convalida</Button>}
                <Button className="btn btn-primary btn-add" onClick={salva} disabled={inCorso || !modificato}>Salva</Button>
            </div>
        </>
    );
};

export default ValidazioneRichiestaFatturazione;
